import React from 'react';
import useMedicalProfiles from './api/useMedicalProfiles';
import { getIntl } from '../utils/intl';
import { usePromptButton } from '../components/shared/PromptButton';
import Text from '../components/v2BaseComponents/Text';
import MainContentContainer from '../components/v2BaseComponents/MainContentContainer';
import { PromptActions } from '../components/v2BaseComponents/Prompt';

const intl = getIntl('RemoveSelfAsCollaboratorButton', {
    deletePrompt: 'Remove Yourself as a Collaborator',
    deleteNotificationHeader: 'Are you sure you want to stop collaborating on this profile?',
    deleteButton: 'Yes',
    cancelButton: 'Cancel',
});

export type UseRemoveSelfAsCollaboratorButtonArgs = {
    profileId: string;
    onRemoveSuccess: () => void;
};

export type UseRemoveSelfAsCollaboratorButtonReturnValue = {
    onPress: () => void;
    modal: React.ReactNode;
    isDeletingProfile: boolean;
};

export const useRemoveSelfAsCollaboratorButton = ({
    profileId,
    onRemoveSuccess,
}: UseRemoveSelfAsCollaboratorButtonArgs) => {
    const { isRemovingSelfAsCollaborator, removeSelfAsCollaborator } = useMedicalProfiles({
        onRemoveSelfAsCollaboratorSuccess: onRemoveSuccess,
    });
    const removeSelfButton = usePromptButton({
        renderPromptContents: (onClose) => (
            <MainContentContainer>
                <Text variant="body-1">{intl('deleteNotificationHeader')}</Text>
                <PromptActions
                    actions={[
                        {
                            title: intl('deleteButton'),
                            onPress: () => {
                                removeSelfAsCollaborator(profileId);
                                onClose();
                            },
                            variant: 'alert',
                            testID: 'remove-self-as-collaborator-button',
                        },
                        {
                            title: intl('cancelButton'),
                            onPress: onClose,
                            variant: 'secondary',
                            testID: 'cancel-self-as-collaborator-button',
                        },
                    ]}
                />
            </MainContentContainer>
        ),
        promptTitle: intl('deletePrompt'),
    });

    return {
        onPress: removeSelfButton.onPress,
        modal: removeSelfButton.Prompt,
        isRemovingSelfAsCollaborator,
    };
};
